import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Camera, MapPin, RefreshCw, X } from 'lucide-react';
import useDialog from '../../hooks/useDialog';
import { formatDistance, isApproximateFix, preciseLocationSteps } from './attendanceStatus';

// A punch is a selfie plus the phone's position. The dialog starts the front camera and asks for
// the position as soon as it opens, takes the photo on a tap, and sends both. The photo is
// mandatory: without a working camera there is no punch. The position is not: a phone that cannot
// place itself still punches, and the server flags the punch for review instead.
//
// The camera needs HTTPS (or localhost) and a user gesture on iPhone; opening this dialog is that
// gesture. The still is shrunk to at most MAX_SIDE pixels and sent as JPEG, about 50 KB.

const MAX_SIDE = 640;
const JPEG_QUALITY = 0.8;
const POSITION_TIMEOUT_MS = 15000;

/** Distance in metres between two positions (haversine), for the "at hospital" line. */
export const distanceMetres = (lat1, lon1, lat2, lon2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

/**
 * What the position line says, given the phone's fix and the hospital's position. A fix away from
 * the hospital that is only an approximate location says so, rather than naming a distance the
 * phone's blurring made up (prod, 2026-10-21: "1.9 km away" for a doctor standing in the clinic).
 */
export const describePosition = (position, hospital) => {
  if (!position) return { text: 'Finding your position…', tone: 'gray' };
  if (!hospital?.configured || hospital.latitude == null) {
    return { text: 'Position captured (hospital position not set)', tone: 'gray' };
  }
  const d = distanceMetres(position.latitude, position.longitude, hospital.latitude, hospital.longitude);
  const within = d <= (hospital.radiusMeters || 150);
  if (within) return { text: `At hospital (${formatDistance(d)})`, tone: 'green' };
  if (isApproximateFix(position.accuracy)) {
    return {
      text: `Your phone is sharing only an approximate location (to about ${formatDistance(position.accuracy)}), so it cannot show you are at the hospital. This punch will be reviewed.`,
      tone: 'amber',
      approximate: true,
    };
  }
  return { text: `Outside hospital (${formatDistance(d)} away): this punch will be reviewed`, tone: 'amber' };
};

const TONE = {
  gray: 'text-gray-600',
  green: 'text-emerald-700',
  amber: 'text-amber-700',
  red: 'text-red-700',
};

const PunchDialog = ({ side, hospital, onPunch, onClose }) => {
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [photo, setPhoto] = useState(null); // { blob, url }
  const [position, setPosition] = useState(null);
  const [positionError, setPositionError] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const dialogRef = useDialog(() => { if (!sending) onClose(); }, { confirmDiscard: false });

  const verb = side === 'in' ? 'Punch in' : 'Punch out';

  // The camera.
  useEffect(() => {
    let live = true;
    let opened = null;
    const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!media?.getUserMedia) {
      setCameraError(window.isSecureContext === false
        ? 'The camera only works over a secure (https) connection.'
        : 'This browser has no camera access.');
      return undefined;
    }
    media.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } }, audio: false })
      .then((s) => {
        if (!live) { s.getTracks().forEach((t) => t.stop()); return; }
        opened = s;
        setStream(s);
      })
      .catch((e) => {
        if (!live) return;
        setCameraError(e?.name === 'NotAllowedError'
          ? 'Camera access was refused. Allow the camera for this site and try again.'
          : 'The camera could not be started.');
      });
    return () => {
      live = false;
      if (opened) opened.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, photo]);

  // The position. One fix, high accuracy, and the punch goes ahead without it if it never comes.
  const locate = useCallback(() => {
    setPosition(null);
    setPositionError(null);
    if (!navigator.geolocation) {
      setPositionError('This browser cannot find your position.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (fix) => setPosition({
        latitude: fix.coords.latitude,
        longitude: fix.coords.longitude,
        accuracy: fix.coords.accuracy,
      }),
      (e) => setPositionError(e?.code === 1
        ? 'Position access was refused. The punch will be reviewed.'
        : 'Your position could not be found. The punch will be reviewed.'),
      { enableHighAccuracy: true, timeout: POSITION_TIMEOUT_MS, maximumAge: 0 },
    );
  }, []);
  useEffect(() => { locate(); }, [locate]);

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const scale = Math.min(1, MAX_SIDE / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d');
    // Mirrored, the way the preview is, so the still looks like what the person saw.
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) { setError('The photo could not be taken. Try again.'); return; }
      setPhoto({ blob, url: URL.createObjectURL(blob) });
    }, 'image/jpeg', JPEG_QUALITY);
  };

  const retake = () => {
    if (photo?.url) URL.revokeObjectURL(photo.url);
    setPhoto(null);
  };

  const submit = async () => {
    if (!photo) return;
    setSending(true);
    setError(null);
    try {
      await onPunch(photo.blob, position);
    } catch (e) {
      setError(e?.message || `${verb} failed.`);
      setSending(false);
    }
  };

  const where = describePosition(position, hospital);

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="punch-title">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <h2 id="punch-title" className="text-lg font-bold text-white">{verb}</h2>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={sending} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="relative mx-auto w-full aspect-square max-h-[60vh] bg-gray-900 rounded-xl overflow-hidden">
            {photo ? (
              <img src={photo.url} alt="Your selfie" className="w-full h-full object-cover" />
            ) : cameraError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 text-white/90">
                <AlertTriangle className="w-8 h-8 text-amber-300 mb-3" />
                <p className="text-sm">{cameraError}</p>
                <p className="text-xs text-white/70 mt-2">A photo is needed for every punch.</p>
              </div>
            ) : (
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
                data-testid="punch-camera"
              />
            )}
          </div>

          <div className={`flex items-start gap-2 text-sm ${TONE[where.tone]}`}>
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span className="flex-1">
              {positionError && !position ? positionError : where.text}
              {where.approximate ? (
                <span className="block text-xs text-gray-600 mt-1">{preciseLocationSteps()} Then tap Retry.</span>
              ) : position?.accuracy != null && position.accuracy > 100 && (
                <span className="block text-xs text-gray-500">Position accuracy is about {formatDistance(position.accuracy)}.</span>
              )}
            </span>
            {(positionError || !position || where.approximate) && (
              <button type="button" onClick={locate} className="text-xs font-medium text-navy-700 hover:underline flex items-center gap-1">
                <RefreshCw className="w-3 h-3" aria-hidden="true" /> Retry
              </button>
            )}
          </div>

          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>

        <div className="px-4 pb-4 pt-1 flex gap-3">
          {photo ? (
            <>
              <button type="button" onClick={retake} disabled={sending} className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 disabled:opacity-60">
                Retake
              </button>
              <button type="button" onClick={submit} disabled={sending} className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-60">
                {sending ? 'Sending…' : verb}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button type="button" onClick={takePhoto} disabled={!stream || !!cameraError} className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2">
                <Camera className="w-5 h-5" aria-hidden="true" /> Take photo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PunchDialog;
